<template>
  <div
    class="base-autocomplete"
    data-test="autocomplete"
    @click="openDropdown"
  >
    <b-form-input
      :id="id"
      v-model.trim="searchedText"
      trim
      @keydown.enter.native.prevent="onKeyEnter"
      @blur.native="onBlur"
      @focus.native="onFocus"
      :placeholder="placeholder"
      data-test="autocomplete-input"
      autocomplete="off"
      v-bind="$attrs"
      v-on="autocompleteListeners"
      :disabled="disabled"
    />
    <div
      class="base-autocomplete__results"
      data-test="autocomplete-results"
      v-if="isOpen"
    >
      <ul
        class="base-autocomplete__list"
        data-test="autocomplete-list"
        ref="ul"
        :style="{'top': moveListBy}"
      >
        <li
          ref="li"
          data-test="autocomplete-item"
          v-for="(result, key) in results"
          :key="key"
          @mouseover="onMouseOver(key)"
          @mousedown.prevent="setResult(result)"
          class="base-autocomplete__result px-3 py-2"
          :class="{ 'is-active': key === activeIndex }"
        >
          {{ result[displayProperty] }}
        </li>
        <li
          v-if="!results.length && type === 'dropdown'"
          data-test="autocomplete-item-not-found"
          class="px-3 py-2"
        >
          No results found
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import helper from '@/core/helper';

export default {
  name: 'BaseAutocomplete',
  props: {
    id: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    displayProperty: {
      type: String,
      default: 'name',
    },
    type: {
      type: String,
      default: 'freeText',
    },
    value: {
      type: [String, Object],
      default: '',
    },
    autoclear: {
      type: Boolean,
      default: false,
    },
    waitLength: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isOpen: false,
      searchedText: '',
      activeIndex: 0,
      moveListBy: '0px',
      isFocused: false,
      wasClosed: false,
    };
  },

  computed: {
    autocompleteListeners() {
      return {
        ...this.$liseners,
        input: this.onInput,
      };
    },
    results() {
      if (this.searchedText.length < this.waitLength) return [];
      return helper.orderBy(this.items
        .filter((item) => item[this.displayProperty].toLowerCase()
          .includes(
            this.searchedText.toLowerCase(),
          )), this.displayProperty);
    },
    isItemInList() {
      return !!this.items.filter(
        (item) => item[this.displayProperty].toLowerCase() === this.searchedText.toLowerCase(),
      ).length;
    },
    isDropdown() {
      return this.type === 'dropdown';
    },
  },

  methods: {
    emitValue(result) {
      const newValue = (this.isDropdown) ? result : result[this.displayProperty];
      this.$emit('input', newValue);
      this.$emit('update:searchText', newValue);
    },
    onFocus(event) {
      this.$emit('focus');
      event.target.select();
      this.isFocused = true;
    },
    onInput(value) {
      if (!this.isFocused) {
        return;
      }
      if (!this.isOpen && !this.wasClosed) {
        this.isOpen = true;
      }
      if (!this.isDropdown) {
        this.$emit('input', this.searchedText);
        if (!this.searchedText) {
          this.$emit('update:searchText', value);
        }
      } else if (!this.results.length || !this.searchedText) {
        this.isOpen = true;
        this.$emit('input', {});
      }
      this.wasClosed = false;
    },
    setResult(result) {
      this.searchedText = result[this.displayProperty];
      this.emitValue(result);
      this.clearInput();
      this.isOpen = false;
      this.wasClosed = true;
      this.$emit('change');
    },

    onKeyEnter() {
      this.isOpen = false;
      this.wasClosed = true;
      this.$emit('submit');
      this.clearInput();
      this.$emit('change');
    },
    onClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        this.activeIndex = 0;
      }
    },
    onMouseOver(key) {
      this.activeIndex = key;
    },
    onBlur($event) {
      this.isFocused = false;
      if (this.isDropdown) {
        if (!this.isItemInList) {
          this.searchedText = '';
          this.$emit('input', {});
          this.isOpen = false;
        }
      }
      this.$emit('blur', $event);
    },
    clearInput() {
      if (this.autoclear) {
        this.searchedText = '';
        this.activeIndex = 0;
      }
    },
    openDropdown() {
      if (!this.disabled && this.isDropdown) {
        this.isOpen = true;
      }
    },
    setDropdownSearchedText() {
      this.searchedText = this.value[this.displayProperty] || '';
    },
  },

  mounted() {
    document.addEventListener('click', this.onClickOutside);
    if (this.value) {
      if (!this.isDropdown) {
        this.searchedText = this.value;
      } else {
        this.setDropdownSearchedText();
      }
    }
  },
  watch: {
    value(newVal, oldVal) {
      if (newVal.id !== oldVal.id) this.setDropdownSearchedText();
      if (typeof newVal === 'object'
          && newVal !== null
          && !Array.isArray(newVal)) return;
      this.searchedText = newVal;
    },
  },

  destroyed() {
    document.removeEventListener('click', this.onClickOutside);
  },
};
</script>

<style lang="scss">
.base-autocomplete {
  position: relative;

  &__results {
    overflow: auto;
    max-height: 200px;
    position: absolute;
    border: 1px solid #eeeeee;
    z-index: 10;
    background: white;
    width: 100%;
    overflow: auto;
  }

  &__list {
    padding: 0;
    margin: 0;
    position: relative;
  }

  &__result {
    list-style: none;
    text-align: left;
    padding: 4px 2px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-active,
    &:hover {
      background-color: $smartology-teal;
      color: white;
    }
  }
}
</style>
